import React, { useEffect } from "react";
import COLORS from "../../constants/colors";
import FlowTopBar from "../common/FlowTopBar";
import HeartView from "../common/HeartView";
import WordPreviewCard from "../common/WordPreviewCard";
import {
  centerSubStyle,
  heroWordStyle,
  mainCardStyle,
  pageStyle,
} from "../../styles/commonStyles";
import { speakFrench } from "../../utils/speech";
import { learningApi } from "../../utils/api";

// 1. 新增日期工具函数（内置，无需额外文件）
const getToday = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

const addDays = (dateStr, days) => {
  const date = new Date(dateStr);
  date.setDate(date.getDate() + days);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

// 2. 新增进度存储函数（持久化到localStorage）
const updateWordProgress = (wordId, progress) => {
  const existing = JSON.parse(localStorage.getItem("wordProgress")) || {};
  existing[wordId] = { ...existing[wordId], ...progress };
  localStorage.setItem("wordProgress", JSON.stringify(existing));
};

export default function LearnPhaseOne({
  word,
  progressText,
  heartCount,
  options,
  triedOptions = [],
  onChooseOption,
  onOpenDetail,
  onPrev,
  onStar,
  onSkip,
  onHome,
  disablePrev = false,
}) {
  if (!word) return null;

  // 3. 判断是否选对答案（学习完成）
  const isLearned = triedOptions.includes(word.translation);

  // 4. 核心：选对后尝试调用后端记录学习结果，失败时回退到 localStorage
  useEffect(() => {
    if (isLearned && word?.id) {
      const learnedAt = getToday(); // 学习完成时间（今日）

      (async () => {
        // 默认本地回退值
        let reviewDue = addDays(learnedAt, 1);

        try {
          // 尝试使用后端接口记录学习结果
          const resp = await learningApi.recordStudyResult(word.id, "correct", 30);
          
          // ✨ 处理碎片奖励
          if (resp && resp.fragments && resp.fragments.length > 0) {
            console.log(`🎉 获得碎片奖励: ${resp.fragments.map(f => f.name).join(', ')}`);
            // TODO: 可以在这里显示碎片奖励提示
          }
          
          // 如果后端返回 next_review_days，则使用它计算到期日
          if (resp && typeof resp.next_review_days !== "undefined" && resp.next_review_days !== null) {
            reviewDue = addDays(learnedAt, resp.next_review_days);
          }
          console.log(`后端记录学习结果成功: ${word.word}`, resp);
        } catch (err) {
          console.warn("后端记录学习结果失败，回退到 localStorage：", err);
        }

        // 无论后端是否成功，更新本地显示缓存以保证 UX
        updateWordProgress(word.id, {
          learnedAt,
          reviewDue,
          reviewCount: 0,
          isLearned: true,
          word: word.word,
          translation: word.translation,
          phonetic: word.phonetic,
          type: word.type,
        });

        // 更新本地学习历史（用于 StatisticsPage 的展示）
        const learnedHistory = JSON.parse(localStorage.getItem("learnedHistory")) || [];
        const today = getToday();
        const todayRecord = learnedHistory.find(item => item.date === today);
        if (todayRecord) {
          todayRecord.count += 1;
        } else {
          learnedHistory.push({ date: today, count: 1 });
        }
        localStorage.setItem("learnedHistory", JSON.stringify(learnedHistory));
      })();
    }
  }, [isLearned, word]);

  return (
    <div style={pageStyle()}>
      <div style={mainCardStyle()}>
        {/* 顶部导航 */}
        <FlowTopBar
          title={`学习进度：${progressText}`}
          onPrev={onPrev}
          onStar={onStar}
          onSkip={onSkip}
          onHome={onHome}
          disablePrev={disablePrev}
        />

        {/* 爱心 */}
        <div style={{ display: "flex", justifyContent: "center", marginBottom: "0.3rem" }}>
          <HeartView count={heartCount} />
        </div>

        {/* 单词 + 发音 */}
        <div style={heroWordStyle()} onClick={() => speakFrench(word.word)}>
          {word.word}
        </div>
        <div
          style={{ ...centerSubStyle(), cursor: "pointer" }}
          onClick={() => speakFrench(word.word)}
        >
          {word.phonetic} 🔊
        </div>

        {/* 汉语选项 */}
        <div style={{ display: "grid", gap: "0.8rem", marginTop: "1.4rem" }}>
          {options.map((opt, i) => {
            const selected = triedOptions.includes(opt);
            const isCorrect = opt === word.translation;

            // 自动生成 A/B/C/D 字母
            const optionLetter = String.fromCharCode(65 + i) + ".";

            return (
              <div key={i}>
                <button
                  onClick={() => onChooseOption(opt)}
                  disabled={isLearned} // 选对后禁用，避免重复存储
                  style={{
                    width: "100%",
                    padding: "1rem",
                    borderRadius: "14px",
                    border: `1px solid ${
                      selected ? (isCorrect ? COLORS.green : COLORS.red) : COLORS.line
                    }`,
                    background: selected
                      ? isCorrect
                        ? COLORS.paleGreen
                        : COLORS.palePink
                      : "#FBFBFD",
                    color: COLORS.text,
                    textAlign: "left",
                    fontSize: "1rem",
                    cursor: isLearned ? "not-allowed" : "pointer",
                    opacity: isLearned ? 0.9 : 1,
                    // flex布局让字母和文字对齐
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  {/* 🔥 ABCD 选项字母（高亮显示） */}
                  <span style={{ fontWeight: 700, color: COLORS.blue, fontSize: "1rem" }}>
                    {optionLetter}
                  </span>
                  <strong style={{ color: COLORS.blue }}>
                    {word.type}
                  </strong>
                  <span style={{ marginLeft: "0.45rem" }}>{opt}</span>
                </button>

                {/* 选对：显示正确单词卡片 */}
                {selected && isCorrect && (
                  <WordPreviewCard word={word} onDetail={onOpenDetail} />
                )}

                {/* 选错:仅显示用户选择的选项作为反馈，不再尝试显示对应的法语翻译 */}
                {selected && !isCorrect && (
                  <div style={{ marginTop: "0.5rem", color: COLORS.red, fontSize: "0.9rem" }}>
                    你选择了: {opt}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}